import type { ComponentType, SVGProps } from "react";

import MicIcon from "@/assets/icons/Mic.svg?react";
import PianoIcon from "@/assets/icons/Piano.svg?react";
import ScissorsIcon from "@/assets/icons/Scissors cut.svg?react";
import WaveIcon from "@/assets/icons/Sound wave.svg?react";
import type { AlbumTrackExtraAssetDto } from "@/lib/lutely-api/gen/dtos";

type VersionMeta = {
  name: string;
  /** What listeners see on the public release page, when it differs. */
  publicName?: string;
  description: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
};

// The versions artists can include, keyed by the API's label, in display order.
const VERSION_META: Record<string, VersionMeta> = {
  Clean: {
    name: "Clean Version",
    description: "Radio-friendly version with no explicit content.",
    Icon: ScissorsIcon,
  },
  Instrumental: {
    name: "Instrumental",
    description: "Beat-only version, no vocals.",
    Icon: PianoIcon,
  },
  Vocals: {
    name: "Vocals",
    publicName: "Acapella",
    description: "Isolated vocal track or acapella.",
    Icon: MicIcon,
  },
};

export const VERSION_LABELS = Object.keys(VERSION_META);

/** Short buyer-facing name: "Clean", "Instrumental", "Acapella". */
export function versionDisplayName(label: string): string {
  const meta = versionMeta(label);
  return meta.publicName ?? meta.name.replace(/ Version$/, "");
}

export function versionMeta(label: string): VersionMeta {
  return (
    VERSION_META[label] ?? { name: label, description: "", Icon: WaveIcon }
  );
}

const FORMATS: Record<string, string> = {
  AUDIO_MPEG: "MP3",
  AUDIO_WAV: "WAV",
  AUDIO_X_WAV: "WAV",
  AUDIO_FLAC: "FLAC",
  AUDIO_AIFF: "AIFF",
  AUDIO_OGG: "OGG",
  AUDIO_WEBM: "WEBM",
};

export function versionFormat(mimeType: string): string | null {
  return FORMATS[mimeType] ?? null;
}

export function versionRank(label: string): number {
  const index = VERSION_LABELS.indexOf(label);
  return index === -1 ? VERSION_LABELS.length : index;
}

/** Display order, WAV before other formats. */
export function sortVersions<
  T extends Pick<AlbumTrackExtraAssetDto, "label" | "mimeType">,
>(versions: T[]): T[] {
  return [...versions].sort(
    (a, b) =>
      versionRank(a.label) - versionRank(b.label) ||
      Number(versionFormat(a.mimeType) !== "WAV") -
        Number(versionFormat(b.mimeType) !== "WAV"),
  );
}
